import { convertEmptyStringsToNull, convertIncomingPercentage, convertOutgoingPercentage } from "../../../pages/activityBuilder/modules/inputs/utils";
import type { IrrigationSystemPayload, IrrigationSystemCreatePayload, IrrigationSystemDeletePayload, IrrigationPhaseCreatePayload, IrrigationPhaseDeletePayload, IrrigationPhasePayload, IrrigationResponse, IrrigationSystemResponse, IrrigationPhaseResponse, ModuleFull, IrrigationPayload } from "../../../pages/activityBuilder/modules/irrigation/irrigationTypes";
import { FEModules } from "../../../utils/moduleList";
import { FEModulesObj } from "../../../utils/moduleList";
import { apiSlice, publicApiSlice, TagTypes } from "../../apiSlice";
import { createModuleGetApi, createEntriesGetApi } from "./factories/createModuleApi";

const MODULE_TYPE = "Irrigation";
const TAG: TagTypes = "Irrigations";
const ENDPOINT = FEModulesObj[FEModules.Irrigation].path;

const irrigationApiSlice = createModuleGetApi<IrrigationPayload>({
  apiSlice,
  path: ENDPOINT,
  moduleName: MODULE_TYPE,
  tag: TAG,
});

const publicIrrigationApiSlice = createModuleGetApi<IrrigationPayload>({
  apiSlice: publicApiSlice,
  path: ENDPOINT,
  moduleName: MODULE_TYPE,
  tag: TAG,
});

export const { useGetIrrigationQuery } = irrigationApiSlice;
export const { useGetIrrigationQuery: usePublicGetIrrigationQuery } = publicIrrigationApiSlice;

const IRRIGATION_SYSTEM_TYPE = "IrrigationSystem";
const IRRIGATION_SYSTEM_TAG: TagTypes = "IrrigationSystems";
const IRRIGATION_SYSTEM_DEFAULT_TAG: TagTypes = "IrrigationSystemsDefaults";
const IRRIGATION_SYSTEM_ENDPOINT = "irrigation-systems";

const irrigationSystemApiSlice = createEntriesGetApi<IrrigationSystemResponse[]>({
  apiSlice,
  path: IRRIGATION_SYSTEM_ENDPOINT,
  moduleName: IRRIGATION_SYSTEM_TYPE,
  tag: IRRIGATION_SYSTEM_TAG,
  tagDefaults: IRRIGATION_SYSTEM_DEFAULT_TAG,
});

const publicIrrigationSystemApiSlice = createEntriesGetApi<IrrigationSystemResponse[]>({
  apiSlice: publicApiSlice,
  path: IRRIGATION_SYSTEM_ENDPOINT,
  moduleName: IRRIGATION_SYSTEM_TYPE,
  tag: IRRIGATION_SYSTEM_TAG,
  tagDefaults: IRRIGATION_SYSTEM_DEFAULT_TAG,
});

export const { useGetIrrigationSystemQuery, useGetIrrigationSystemDefaultsQuery } = irrigationSystemApiSlice;
export const {
  useGetIrrigationSystemQuery: usePublicGetIrrigationSystemQuery,
  useGetIrrigationSystemDefaultsQuery: usePublicGetIrrigationSystemDefaultsQuery
} = publicIrrigationSystemApiSlice;

const IRRIGATION_PHASE_TYPE = "IrrigationPhase";
const IRRIGATION_PHASE_TAG: TagTypes = "IrrigationPhases";
const IRRIGATION_PHASE_DEFAULT_TAG: TagTypes = "IrrigationPhasesDefaults";
const IRRIGATION_PHASE_ENDPOINT = "irrigation-phases";

const transformIrrigationPhaseResponse = (response: IrrigationPhaseResponse[]) => {
  for (const phase of response)
    convertIncomingPercentage(phase, ["transmission_loss_t2", "pumping_efficiency_t2"])
  return response
}

const irrigationPhaseApiSlice = createEntriesGetApi<IrrigationPhaseResponse[]>({
  apiSlice,
  path: IRRIGATION_PHASE_ENDPOINT,
  moduleName: IRRIGATION_PHASE_TYPE,
  tag: IRRIGATION_PHASE_TAG,
  tagDefaults: IRRIGATION_PHASE_DEFAULT_TAG,
  transformResponse: transformIrrigationPhaseResponse,
});

const publicIrrigationPhaseApiSlice = createEntriesGetApi<IrrigationPhaseResponse[]>({
  apiSlice: publicApiSlice,
  path: IRRIGATION_PHASE_ENDPOINT,
  moduleName: IRRIGATION_PHASE_TYPE,
  tag: IRRIGATION_PHASE_TAG,
  tagDefaults: IRRIGATION_PHASE_DEFAULT_TAG,
  transformResponse: transformIrrigationPhaseResponse,
});

export const { useGetIrrigationPhaseQuery, useGetIrrigationPhaseDefaultsQuery } = irrigationPhaseApiSlice;
export const {
  useGetIrrigationPhaseQuery: usePublicGetIrrigationPhaseQuery,
  useGetIrrigationPhaseDefaultsQuery: usePublicGetIrrigationPhaseDefaultsQuery
} = publicIrrigationPhaseApiSlice;

export const moduleApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    updateIrrigations: builder.mutation<ModuleFull, { data: ModuleFull, activityId: number }>({
      query: ({ data, activityId }) => {
        const payload = { ...data.module  } as unknown as IrrigationResponse
        const { id, ...rest } = payload
        const body = { ...rest, activityId }
        convertEmptyStringsToNull(body)
        return { url: `irrigations/${id}/`, method: 'PUT', body }
      },
      invalidatesTags: (_results, _error, { activityId, data }) => [
        { type: TAG, id: data.module.id ?? undefined },
        { type: 'ModuleResults', id: data.module.id ? `irrigations-${data.module.id}` : undefined },
        { type: "ModuleHistory", id: data.module.id ? `irrigations-${data.module.id}` : undefined },
        { type: "ModulesStatuses", id: activityId },
      ]
    }),

    createIrrigationSystem: builder.mutation<IrrigationSystemResponse, IrrigationSystemCreatePayload>({
      query: ({ body }) => ({ url: "irrigation-systems/", method: 'POST', body }),
      invalidatesTags: (_results, _error, { activityId, body }) => [
        { type: 'IrrigationSystems', id: activityId },
        { type: 'ModuleResults', id: body.parent ? `irrigations-${body.parent}` : undefined },
        { type: "ModuleHistory", id: body.parent ? `irrigations-${body.parent}` : undefined },
        { type: "ModulesStatuses", id: activityId },
      ]
    }),
    updateIrrigationSystem: builder.mutation<IrrigationSystemResponse, IrrigationSystemPayload>({
      query: ({ body }) => {
        const { id, ...rest } = body
        convertEmptyStringsToNull(rest);
        return { url: `irrigation-systems/${id}/`, method: 'PUT', body: rest }
      },
      invalidatesTags: (_results, _error, { activityId, body }) => [
        { type: 'IrrigationSystems', id: activityId },
        { type: "ModuleHistory", id: body.parent ? `irrigations-${body.parent}` : undefined },
        { type: "IrrigationSystemsDefaults", id: body.id ? body.id : undefined },
        { type: "ModulesStatuses", id: activityId }]
    }),
    deleteIrrigationSystem: builder.mutation<IrrigationSystemResponse, IrrigationSystemDeletePayload>({
      query: ({ id }) => ({ url: `irrigation-systems/${id}/`, method: 'DELETE' }),
      invalidatesTags: (_results, _error, { activityId, parent, id }) => [
        { type: 'IrrigationSystems', id: activityId },
        { type: 'ModuleResults', id: parent ? `irrigations-${parent}` : undefined },
        { type: "ModuleHistory", id: parent ? `irrigations-${parent}` : undefined },
        { type: "IrrigationSystemsDefaults", id: id },
        { type: "ModulesStatuses", id: activityId },
      ]
    }),

    createIrrigationPhase: builder.mutation<IrrigationPhaseResponse, IrrigationPhaseCreatePayload>({
      query: ({ body }) => ({ url: "irrigation-phases/", method: 'POST', body }),
      invalidatesTags: (_results, _error, { activityId, body }) => [
        { type: 'IrrigationPhases', id: activityId },
        { type: 'ModuleResults', id: body.parent ? `irrigations-${body.parent}` : undefined },
        { type: "ModuleHistory", id: body.parent ? `irrigations-${body.parent}` : undefined },
        { type: "ModulesStatuses", id: activityId }
      ]
    }),
    updateIrrigationPhase: builder.mutation<IrrigationPhaseResponse, IrrigationPhasePayload>({
      query: ({ body }) => {
        const module = { ...body }
        convertEmptyStringsToNull(module);
        convertOutgoingPercentage(module, ["transmission_loss_t2", "pumping_efficiency_t2"])
        const { id, ...rest } = module

        return { url: `irrigation-phases/${id}/`, method: 'PUT', body: rest }
      },
      invalidatesTags: (_results, _error, { activityId, body }) => [
        { type: 'IrrigationPhases', id: activityId },
        { type: "ModuleHistory", id: body.parent ? `irrigations-${body.parent}` : undefined },
        { type: "IrrigationPhasesDefaults", id: body?.id ? body?.id : undefined },
        { type: "ModulesStatuses", id: activityId }],
    }),
    deleteIrrigationPhase: builder.mutation<IrrigationPhaseResponse, IrrigationPhaseDeletePayload>({
      query: ({ id }) => ({ url: `irrigation-phases/${id}/`, method: 'DELETE' }),
      invalidatesTags: (_results, _error, { activityId, parent, id }) => [
        { type: 'IrrigationPhases', id: activityId },
        { type: 'ModuleResults', id: parent ? `irrigations-${parent}` : undefined },
        { type: "ModuleHistory", id: parent ? `irrigations-${parent}` : undefined },
        { type: "IrrigationPhasesDefaults", id },
        { type: "ModulesStatuses", id: activityId }
      ]
    }),
  })
})

export const {
  useUpdateIrrigationsMutation,

  useUpdateIrrigationPhaseMutation,
  useCreateIrrigationPhaseMutation,
  useDeleteIrrigationPhaseMutation,

  useUpdateIrrigationSystemMutation,
  useCreateIrrigationSystemMutation,
  useDeleteIrrigationSystemMutation,
} = moduleApiSlice;